import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconActivity,
  IconAdjustments,
  IconBan,
  IconBrandBinance,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCoin,
  IconCopy,
  IconDeviceFloppy,
  IconDownload,
  IconExternalLink,
  IconInfoCircle,
  IconPlus,
  IconRocket,
  IconRoute,
  IconSparkles,
  IconTargetArrow,
  IconTrash,
  IconTrendingUp,
  IconWallet,
  IconX,
  type TablerIcon,
} from '@tabler/icons-react';
import {
  BNB_SCALAR_FILTER_KEYS,
  DEFAULT_BNB_PRESET,
  BnbExportCreated,
  BnbExportTask,
  BnbFilters,
  BnbMetricMap,
  BnbPeriod,
  BnbPreset,
  BnbPresetPayload,
  BnbScalarFilterKey,
  BnbWinrateBucket,
  bnbPresetsClient,
} from '../../api/bnbPresetsClient';
import './bnbPresets.css';

type RangeDefinition = {
  label: string;
  min: BnbScalarFilterKey;
  max: BnbScalarFilterKey;
  unit?: string;
  help: string;
  step?: number | 'any';
  suggestions?: Array<{ label: string; min: number | null; max: number | null }>;
};

type FilterSectionDefinition = {
  id: string;
  title: string;
  description: string;
  emoji: string;
  icon: TablerIcon;
  ranges: RangeDefinition[];
};

const PERFORMANCE_RANGES: RangeDefinition[] = [
  {
    label: 'Wallet ROI', min: 'min_roi', max: 'max_roi', unit: '%',
    help: 'Overall lifetime return across tokens with at least one sell.',
    suggestions: [
      { label: 'Positive', min: 0, max: null },
      { label: '2×+', min: 100, max: null },
      { label: '0–500%', min: 0, max: 500 },
    ],
  },
  {
    label: 'PNL', min: 'min_pnl_usd', max: 'max_pnl_usd', unit: 'USD',
    help: 'Earned USD minus spent USD for sold or partially sold tokens.',
    suggestions: [
      { label: 'Profit only', min: 0, max: null },
      { label: '$100+', min: 100, max: null },
    ],
  },
];

const FILTER_SECTIONS: FilterSectionDefinition[] = [
  {
    id: 'token-edge',
    title: 'Token outcomes',
    description: 'Typical token result, less distorted by a single lucky trade.',
    emoji: '📈',
    icon: IconTrendingUp,
    ranges: [
      {
        label: 'Median token ROI', min: 'min_median_token_roi', max: 'max_median_token_roi', unit: '%',
        help: 'Middle ROI across closed tokens. Median is more resistant to outliers.',
        suggestions: [{ label: 'Positive', min: 0, max: null }, { label: '0–300%', min: 0, max: 300 }],
      },
      {
        label: 'Average token ROI', min: 'min_avg_token_roi', max: 'max_avg_token_roi', unit: '%',
        help: 'Arithmetic average ROI across tokens with at least one sell.',
      },
    ],
  },
  {
    id: 'activity',
    title: 'Trading activity',
    description: 'How broad and frequent the wallet activity is.',
    emoji: '⚡',
    icon: IconActivity,
    ranges: [
      {
        label: 'Traded tokens', min: 'min_total_traded_tokens', max: 'max_total_traded_tokens', unit: 'tokens', step: 1,
        help: 'All tokens active in the selected period, including tokens without sells.',
        suggestions: [{ label: '5+', min: 5, max: null }, { label: '10–100', min: 10, max: 100 }],
      },
      {
        label: 'Tokens with sells', min: 'min_tokens_with_sells_count', max: 'max_tokens_with_sells_count', unit: 'tokens', step: 1,
        help: 'Tokens where the wallet made at least one sell.',
      },
      {
        label: 'Tokens without sells', min: 'min_tokens_without_sells_count', max: 'max_tokens_without_sells_count', unit: 'tokens', step: 1,
        help: 'Tokens that were bought but never sold.',
      },
      {
        label: 'Unsold token share', min: 'min_tokens_without_sells_percent', max: 'max_tokens_without_sells_percent', unit: '%',
        help: 'Share of period tokens with no sells. A lower cap filters out abandoned positions.',
        suggestions: [{ label: '≤20%', min: null, max: 20 }, { label: '≤50%', min: null, max: 50 }],
      },
      {
        label: 'Total swaps', min: 'min_total_swaps_count', max: 'max_total_swaps_count', unit: 'swaps', step: 1,
        help: 'All buys and sells across period tokens.',
      },
      {
        label: 'Total buys', min: 'min_total_buys_count', max: 'max_total_buys_count', unit: 'buys', step: 1,
        help: 'Buy swaps across all period tokens.',
      },
      {
        label: 'Total sells', min: 'min_total_sells_count', max: 'max_total_sells_count', unit: 'sells', step: 1,
        help: 'Sell swaps across all period tokens.',
      },
    ],
  },
  {
    id: 'capital',
    title: 'Capital flow',
    description: 'USD volume split between closed and still-open positions.',
    emoji: '💰',
    icon: IconWallet,
    ranges: [
      { label: 'Spent on sold tokens', min: 'min_spent_usd', max: 'max_spent_usd', unit: 'USD', help: 'Purchases only for tokens that have at least one sell.' },
      { label: 'Earned from sells', min: 'min_earned_usd', max: 'max_earned_usd', unit: 'USD', help: 'USD value received from token sells.' },
      { label: 'Spent on all tokens', min: 'min_all_tokens_spent_usd', max: 'max_all_tokens_spent_usd', unit: 'USD', help: 'Purchases of every period token, whether sold or not.' },
      { label: 'Spent without sells', min: 'min_tokens_without_sells_spent_usd', max: 'max_tokens_without_sells_spent_usd', unit: 'USD', help: 'Capital put into tokens that have never been sold.' },
    ],
  },
  {
    id: 'positions',
    title: 'Position behavior',
    description: 'Entry cadence and how completely the wallet exits positions.',
    emoji: '🎯',
    icon: IconCoin,
    ranges: [
      { label: 'Average buys per token', min: 'min_avg_token_buys', max: 'max_avg_token_buys', help: 'Average buy count for tokens with at least one sell.' },
      { label: 'Average sells per token', min: 'min_avg_token_sells', max: 'max_avg_token_sells', help: 'Average sell count for tokens with at least one sell.' },
      { label: 'Average position sold', min: 'min_avg_avg_sell_percent', max: 'max_avg_avg_sell_percent', unit: '%', help: 'Average percentage of the bought amount that was sold.' },
      { label: 'Median position sold', min: 'min_median_avg_token_sell_percent', max: 'max_median_avg_token_sell_percent', unit: '%', help: 'Median sold percentage across tokens.' },
      { label: 'Average token unsold', min: 'min_avg_token_unsold_percent', max: 'max_avg_token_unsold_percent', unit: '%', help: 'Average percentage of the bought token amount that remains unsold.' },
      { label: 'Not-closed positions', min: 'min_not_closed_positions_tokens_percent', max: 'max_not_closed_positions_tokens_percent', unit: '%', help: 'Share of tokens where sold amount is below bought amount.' },
    ],
  },
  {
    id: 'quality',
    title: 'Quality & routing',
    description: 'Data-quality guardrails and execution complexity.',
    emoji: '🛡️',
    icon: IconRoute,
    ranges: [
      {
        label: 'Sold before buy', min: 'min_sold_before_buy_tokens_percent', max: 'max_sold_before_buy_tokens_percent', unit: '%',
        help: 'Tokens where the first observed sell predates the first observed buy.',
        suggestions: [{ label: 'None', min: null, max: 0 }, { label: '≤5%', min: null, max: 5 }],
      },
      {
        label: 'Sold more than bought', min: 'min_sold_more_than_bought_tokens_percent', max: 'max_sold_more_than_bought_tokens_percent', unit: '%',
        help: 'Tokens where observed sold amount exceeds observed bought amount.',
        suggestions: [{ label: '≤10%', min: null, max: 10 }, { label: '≤20%', min: null, max: 20 }],
      },
      { label: 'Router swaps', min: 'min_router_swaps_count', max: 'max_router_swaps_count', unit: 'swaps', step: 1, help: 'Swaps routed through an aggregator or router.' },
      { label: 'Source events', min: 'min_source_events_count', max: 'max_source_events_count', unit: 'events', step: 1, help: 'Raw Swap events behind normalized swaps.' },
      { label: 'Multi-event swap tokens', min: 'min_multi_event_swaps_token_percent', max: 'max_multi_event_swaps_token_percent', unit: '%', help: 'Share of tokens with at least one multi-event swap.' },
    ],
  },
];

const DURATION_RANGES: RangeDefinition[] = [
  { label: 'Median trade duration', min: 'min_median_token_trade_duration_seconds', max: 'max_median_token_trade_duration_seconds', help: 'Median time from first buy to last sell.' },
  { label: 'Average trade duration', min: 'min_avg_token_trade_duration_seconds', max: 'max_avg_token_trade_duration_seconds', help: 'Average time from first buy to last sell.' },
  { label: 'Time to first sell', min: 'min_avg_token_seconds_first_sell_duration', max: 'max_avg_token_seconds_first_sell_duration', help: 'Average time from first buy to the first sell.' },
];

const SUMMARY_SORT_OPTIONS = [
  ['last_swap_block_timestamp', 'Last swap'], ['first_swap_block_timestamp', 'First swap'],
  ['winrate', 'Winrate'], ['roi', 'ROI'], ['pnl_usd', 'PNL'], ['spent_usd', 'Spent'],
  ['earned_usd', 'Earned'], ['all_tokens_spent_usd', 'All-token spent'],
  ['total_traded_tokens', 'Traded tokens'], ['tokens_with_sells_count', 'Tokens with sells'],
  ['tokens_without_sells_count', 'Tokens without sells'], ['total_buys_count', 'Buys'],
  ['total_sells_count', 'Sells'], ['total_swaps_count', 'Swaps'],
  ['median_token_roi', 'Median token ROI'], ['avg_token_roi', 'Average token ROI'],
  ['median_token_trade_duration_seconds', 'Median duration'], ['avg_token_trade_duration_seconds', 'Average duration'],
  ['router_swaps_count', 'Router swaps'], ['source_events_count', 'Source events'],
  ['rocket_x2_count', 'Rockets ×2'], ['rocket_x5_count', 'Rockets ×5'], ['rocket_x10_count', 'Rockets ×10'],
];

const TOKEN_SORT_OPTIONS = [
  ['last_swap_block_timestamp', 'Last swap'], ['first_swap_block_timestamp', 'First swap'],
  ['roi', 'ROI'], ['pnl_usd', 'PNL'], ['total_spent_usd', 'Spent'],
  ['total_earned_usd', 'Earned'], ['total_swaps_count', 'Swaps'], ['avg_sell_percent', 'Average sold %'],
  ['first_buy_token_price_usd', 'First-buy price'], ['last_sell_token_price_usd', 'Last-sell price'],
  ['weighted_avg_buy_token_price_usd', 'Weighted buy price'], ['weighted_avg_sell_token_price_usd', 'Weighted sell price'],
];

const PERIODS: BnbPeriod[] = [1, 7, 14, 30, 45, 90];
const TERMINAL_EXPORT_STATUSES = new Set(['done', 'error', 'canceled']);
const BNB_AMOUNT_PATTERN = /^\d+(?:\.\d{1,18})?$/;
const BNB_TOKEN_ADDRESS_PATTERN = /\b0x[a-f0-9]{40}\b/gi;

export const extractBnbTokenAddresses = (text: string) => {
  const unique = new Map<string, string>();
  (text.match(BNB_TOKEN_ADDRESS_PATTERN) || []).forEach((address) => {
    const normalized = `0x${address.slice(2)}`;
    const key = normalized.toLowerCase();
    if (!unique.has(key)) unique.set(key, normalized);
  });
  return Array.from(unique.values());
};

export const canonicalBnbAmount = (value: string) => {
  const [wholePart = '0', fractionPart = ''] = value.trim().replace(',', '.').split('.');
  const whole = wholePart.replace(/^0+(?=\d)/, '') || '0';
  const fraction = fractionPart.replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
};

export const isBnbAmount = (value: string) => BNB_AMOUNT_PATTERN.test(value.trim().replace(',', '.'));

export const compareBnbAmounts = (left: string, right: string) => {
  const [leftWhole, leftFraction = ''] = canonicalBnbAmount(left).split('.');
  const [rightWhole, rightFraction = ''] = canonicalBnbAmount(right).split('.');
  if (leftWhole.length !== rightWhole.length) return leftWhole.length - rightWhole.length;
  if (leftWhole !== rightWhole) return leftWhole > rightWhole ? 1 : -1;
  const fractionLength = Math.max(leftFraction.length, rightFraction.length);
  const paddedLeft = leftFraction.padEnd(fractionLength, '0');
  const paddedRight = rightFraction.padEnd(fractionLength, '0');
  return paddedLeft === paddedRight ? 0 : paddedLeft > paddedRight ? 1 : -1;
};

const clonePreset = (preset: BnbPreset): BnbPreset => JSON.parse(JSON.stringify(preset));

const normalizePreset = (preset: BnbPreset): BnbPreset => JSON.parse(JSON.stringify({
  ...preset,
  filters: {
    ...preset.filters,
    quote_tokens: preset.filters?.quote_tokens || {},
    projects: preset.filters?.projects || {},
    trade_providers: preset.filters?.trade_providers || {},
    bnb_balance_min: preset.filters?.bnb_balance_min == null ? null : String(preset.filters.bnb_balance_min),
    bnb_balance_max: preset.filters?.bnb_balance_max == null ? null : String(preset.filters.bnb_balance_max),
  },
}));

const toPayload = ({ id: _id, filters, ...payload }: BnbPreset): BnbPresetPayload => ({
  ...payload,
  filters: {
    ...Object.fromEntries(
      BNB_SCALAR_FILTER_KEYS
        .filter((key) => filters[key] != null)
        .map((key) => [key, filters[key]]),
    ),
    ...(filters.bnb_balance_min != null ? { bnb_balance_min: canonicalBnbAmount(filters.bnb_balance_min) } : {}),
    ...(filters.bnb_balance_max != null ? { bnb_balance_max: canonicalBnbAmount(filters.bnb_balance_max) } : {}),
    quote_tokens: filters.quote_tokens || {},
    projects: filters.projects || {},
    trade_providers: filters.trade_providers || {},
  },
});
const readNumber = (value: string): number | null => value === '' ? null : Number(value);

const formatDuration = (seconds: number | null | undefined) => {
  if (seconds == null) return '';
  if (seconds === 0) return '0s';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${days ? `${days}d` : ''}${hours ? `${hours}h` : ''}${minutes ? `${minutes}m` : ''}${secs ? `${secs}s` : ''}` || '0s';
};

const parseDuration = (input: string): number | null => {
  const value = input.trim().toLowerCase();
  if (!value) return null;
  if (/^\d+(\.\d+)?$/.test(value)) return Number(value);
  const compact = value.replace(/\s/g, '');
  const pattern = /(\d+(?:\.\d+)?)(d|h|m|s)/g;
  let consumed = '';
  let total = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(compact)) !== null) {
    consumed += match[0];
    const multiplier = match[2] === 'd' ? 86400 : match[2] === 'h' ? 3600 : match[2] === 'm' ? 60 : 1;
    total += Number(match[1]) * multiplier;
  }
  return consumed && consumed === compact ? total : null;
};

const friendlyStatus = (status: string) => ({
  created: 'Queued', queued_release: 'Waiting for release', analyzing_wallets: 'Analyzing wallets',
  loading_tokens: 'Loading tokens', building_files: 'Building files', done: 'Delivered',
  error: 'Export failed', canceled: 'Canceled',
}[status] || status);

function HelpTip({ text, title = 'How it works' }: { text: string; title?: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <span className="bnb-help" ref={rootRef}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-label={`Help: ${title}`} aria-expanded={open}>
        <IconInfoCircle size={14} />
      </button>
      {open && (
        <span className="bnb-help-popover" role="tooltip">
          <span className="bnb-help-popover-head">
            <IconInfoCircle size={13} />
            <strong>{title}</strong>
            <button type="button" aria-label="Close help" onClick={() => setOpen(false)}><IconX size={12} /></button>
          </span>
          <span>{text}</span>
        </span>
      )}
    </span>
  );
}

function FormatPicker({ value, onChange }: { value: BnbPreset['format_type']; onChange: (value: BnbPreset['format_type']) => void }) {
  return (
    <div className="bnb-format-picker" aria-label="Export format">
      {([
        ['EXCEL', 'Excel'], ['TXT_EXCEL', 'Excel + TXT'], ['TXT', 'TXT'],
      ] as Array<[BnbPreset['format_type'], string]>).map(([format, label]) => (
        <button type="button" className={value === format ? 'active' : ''} key={format} onClick={() => onChange(format)}>{label}</button>
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, label, detail }: { checked: boolean; onChange: (value: boolean) => void; label: string; detail?: string }) {
  return (
    <label className="bnb-toggle-row">
      <span><strong>{label}</strong>{detail && <small>{detail}</small>}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.currentTarget.checked)} />
      <i aria-hidden="true"><b /></i>
    </label>
  );
}

function BnbAmountInput({ label, value, onChange }: {
  label: 'FROM' | 'TO';
  value: string | null | undefined;
  onChange: (value: string | null) => void;
}) {
  const [text, setText] = useState(value ?? '');
  const [error, setError] = useState(false);

  useEffect(() => setText(value ?? ''), [value]);

  const commit = () => {
    const normalized = text.trim().replace(',', '.');
    if (!normalized) {
      setError(false);
      setText('');
      onChange(null);
      return;
    }
    if (!isBnbAmount(normalized)) {
      setError(true);
      return;
    }
    const canonical = canonicalBnbAmount(normalized);
    setText(canonical);
    setError(false);
    onChange(canonical);
  };

  return (
    <label className={error ? 'invalid' : ''}>
      <span>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        aria-label={`BNB ${label === 'FROM' ? 'minimum' : 'maximum'}`}
        aria-invalid={error}
        placeholder={label === 'FROM' ? 'No minimum' : 'No maximum'}
        value={text}
        onChange={(event) => { setText(event.currentTarget.value); setError(false); }}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
      />
      {error && <small>Use a positive BNB amount with up to 18 decimals.</small>}
    </label>
  );
}

function BnbBalanceControl({ min, max, onMinChange, onMaxChange }: {
  min: string | null | undefined;
  max: string | null | undefined;
  onMinChange: (value: string | null) => void;
  onMaxChange: (value: string | null) => void;
}) {
  const active = min != null || max != null;
  const invalidRange = min != null && max != null && isBnbAmount(min) && isBnbAmount(max) && compareBnbAmounts(min, max) > 0;
  return (
    <div className={`bnb-native-balance-control ${active ? 'active' : ''}`}>
      <div className="bnb-native-balance-head">
        <div className="bnb-native-balance-symbol">BNB</div>
        <div><strong>Native BNB balance</strong><span>RPC balance checked after wallet metrics</span></div>
        <HelpTip title="BNB balance" text="The native BNB balance is checked after all other filters. Decimal strings keep the full 18-digit precision required by the API." />
        {active && <button type="button" title="Clear BNB balance" onClick={() => { onMinChange(null); onMaxChange(null); }}><IconBan size={14} /></button>}
      </div>
      <div className="bnb-native-balance-inputs">
        <BnbAmountInput label="FROM" value={min} onChange={onMinChange} />
        <BnbAmountInput label="TO" value={max} onChange={onMaxChange} />
      </div>
      <div className="bnb-native-balance-presets">
        <button type="button" onClick={() => { onMinChange('0.1'); onMaxChange(null); }}>0.1+</button>
        <button type="button" onClick={() => { onMinChange('0.5'); onMaxChange(null); }}>0.5+</button>
        <button type="button" onClick={() => { onMinChange('1'); onMaxChange('10'); }}>1–10</button>
        <button type="button" onClick={() => { onMinChange('10'); onMaxChange(null); }}>10+</button>
      </div>
      {invalidRange && <div className="bnb-native-balance-error">Minimum BNB balance cannot exceed maximum.</div>}
    </div>
  );
}

function RangeField({ definition, filters, onChange }: {
  definition: RangeDefinition;
  filters: BnbFilters;
  onChange: (key: BnbScalarFilterKey, value: number | null) => void;
}) {
  const min = filters[definition.min] ?? null;
  const max = filters[definition.max] ?? null;
  const [showMax, setShowMax] = useState(max != null);
  const active = min != null || max != null;

  useEffect(() => { if (max != null) setShowMax(true); }, [max]);

  return (
    <div className={`bnb-smart-field ${active ? 'active' : ''}`}>
      <div className="bnb-smart-field-head">
        <div className="bnb-field-label">
          <strong>{definition.label}</strong>
          {definition.unit && <span>{definition.unit}</span>}
          <HelpTip text={definition.help} title={definition.label} />
        </div>
        {active && (
          <button className="bnb-clear-field" type="button" title="Disable this filter" onClick={() => {
            onChange(definition.min, null);
            onChange(definition.max, null);
            setShowMax(false);
          }}><IconBan size={14} /></button>
        )}
      </div>
      <div className="bnb-threshold-inputs">
        <label>
          <span>FROM</span>
          <input type="number" step={definition.step || 'any'} inputMode="decimal" placeholder="No minimum" value={min ?? ''} onChange={(event) => onChange(definition.min, readNumber(event.currentTarget.value))} />
        </label>
        {showMax ? (
          <label>
            <span>TO</span>
            <input type="number" step={definition.step || 'any'} inputMode="decimal" placeholder="No maximum" value={max ?? ''} onChange={(event) => onChange(definition.max, readNumber(event.currentTarget.value))} />
          </label>
        ) : (
          <button className="bnb-add-cap" type="button" onClick={() => setShowMax(true)}><IconPlus size={13} /> add upper limit</button>
        )}
      </div>
      {definition.suggestions && (
        <div className="bnb-suggestion-row">
          {definition.suggestions.map((suggestion) => (
            <button type="button" key={suggestion.label} onClick={() => {
              onChange(definition.min, suggestion.min);
              onChange(definition.max, suggestion.max);
              setShowMax(suggestion.max != null);
            }}>{suggestion.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

function WinrateSpectrum({ buckets, loading, error, min, max, period }: {
  buckets: BnbWinrateBucket[] | null;
  loading: boolean;
  error: string | null;
  min: number;
  max: number;
  period: BnbPeriod;
}) {
  const [focused, setFocused] = useState<BnbWinrateBucket | null>(null);
  const visibleBuckets = (buckets || []).filter((bucket) => bucket.winrate_percent > 0 && bucket.winrate_percent < 100);
  const bucketsByWinrate = new Map(visibleBuckets.map((bucket) => [bucket.winrate_percent, bucket]));
  const smoothingWeights = [1, 2, 3, 4, 3, 2, 1];
  const plotBuckets = Array.from({ length: 99 }, (_, index) => {
    const winrate = index + 1;
    let weightedCount = 0;
    let totalWeight = 0;
    smoothingWeights.forEach((weight, weightIndex) => {
      const neighbor = winrate + weightIndex - 3;
      if (neighbor < 1 || neighbor > 99) return;
      weightedCount += (bucketsByWinrate.get(neighbor)?.wallets_count || 0) * weight;
      totalWeight += weight;
    });
    return {
      bucket: bucketsByWinrate.get(winrate) || { winrate_percent: winrate, wallets_count: 0, percent: 0 },
      smoothedCount: totalWeight > 0 ? weightedCount / totalWeight : 0,
    };
  });
  const positiveTrend = plotBuckets.map((point) => point.smoothedCount).filter((count) => count > 0).sort((left, right) => left - right);
  const trendP92 = positiveTrend[Math.floor((positiveTrend.length - 1) * 0.92)] || 1;
  const visualCeiling = Math.max(1, trendP92 * 1.18);
  const chartPoints = plotBuckets.map((point, index) => {
    const visualRatio = Math.min(point.smoothedCount, visualCeiling) / visualCeiling;
    const height = visualRatio > 0 ? Math.max(1.2, visualRatio * 48) : 0;
    return { ...point, x: index + 0.5, y: 51 - height, height };
  });
  const trendLine = chartPoints.reduce((path, point, index, points) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    const middle = (previous.x + point.x) / 2;
    return `${path} C ${middle} ${previous.y}, ${middle} ${point.y}, ${point.x} ${point.y}`;
  }, '');
  const trendArea = `${trendLine} L 98.5 51 L 0.5 51 Z`;
  const totalCount = visibleBuckets.reduce((total, bucket) => total + bucket.wallets_count, 0);
  const selectedCount = visibleBuckets
    .filter((bucket) => bucket.winrate_percent >= min && bucket.winrate_percent <= max)
    .reduce((total, bucket) => total + bucket.wallets_count, 0);
  const selectedPercent = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0;

  useEffect(() => setFocused(null), [period]);

  return (
    <div className="bnb-winrate-spectrum">
      <div className="bnb-spectrum-head">
        <span><IconChartBar size={13} />{period}D WALLET DISTRIBUTION</span>
        {!loading && totalCount > 0 && <strong>{selectedPercent.toFixed(1)}% <small>in range</small></strong>}
      </div>
      <div className={`bnb-spectrum-chart ${loading ? 'loading' : ''}`}>
        {loading ? (
          <div className="bnb-spectrum-skeleton" aria-label="Loading winrate distribution">{Array.from({ length: 34 }, (_, index) => <i key={index} style={{ height: `${18 + ((index * 17) % 70)}%` }} />)}</div>
        ) : visibleBuckets.length > 0 ? (
          <svg key={period} viewBox="0 0 99 52" preserveAspectRatio="none" role="img" aria-label={`${period}-day winrate distribution`} onPointerLeave={(event) => { if (event.pointerType === 'mouse') setFocused(null); }}>
            <path className="bnb-spectrum-area" d={trendArea} />
            {chartPoints.map((point) => {
              const { bucket } = point;
              const selected = bucket.winrate_percent >= min && bucket.winrate_percent <= max;
              return (
                <rect
                  className={selected ? 'selected' : ''}
                  key={bucket.winrate_percent}
                  x={bucket.winrate_percent - 0.92}
                  y={point.y}
                  width="0.84"
                  height={point.height}
                  rx="0.18"
                  onPointerEnter={() => setFocused(bucket)}
                  onPointerDown={() => setFocused(bucket)}
                >
                  <title>{bucket.winrate_percent}–{bucket.winrate_percent + 1}% · {bucket.wallets_count.toLocaleString()} wallets · {bucket.percent.toFixed(2)}%</title>
                </rect>
              );
            })}
          </svg>
        ) : (
          <div className="bnb-spectrum-empty" title={error || undefined}>{error ? `No spectrum for ${period}d` : `No wallets in the ${period}d sample`}</div>
        )}
      </div>
      <div className="bnb-spectrum-axis"><span>1%</span><span>25</span><span>50</span><span>75</span><span>99%</span></div>
      <div className="bnb-spectrum-caption">
        {focused
          ? <strong>{focused.winrate_percent}–{focused.winrate_percent + 1}% <span>{focused.wallets_count.toLocaleString()} wallets · {focused.percent.toFixed(2)}%</span></strong>
          : error
            ? <span>{error.includes('one of') ? 'Spectrum is available for 7d and 30d' : 'Could not load this period'}</span>
            : <span>{period}d sample · smoothed 1–99%{totalCount > 0 && <> · {compactNumber.format(selectedCount)} of {compactNumber.format(totalCount)} selected</>}</span>}
      </div>
    </div>
  );
}

function WinrateControl({ filters, onChange, spectrum, spectrumLoading, spectrumError, period }: {
  filters: BnbFilters;
  onChange: (key: BnbScalarFilterKey, value: number | null) => void;
  spectrum: BnbWinrateBucket[] | null;
  spectrumLoading: boolean;
  spectrumError: string | null;
  period: BnbPeriod;
}) {
  const min = filters.min_winrate ?? 0;
  const max = filters.max_winrate ?? 100;
  const setMin = (value: number) => onChange('min_winrate', value <= 0 ? null : Math.min(value, max - 1));
  const setMax = (value: number) => onChange('max_winrate', value >= 100 ? null : Math.max(value, min + 1));

  return (
    <div className="bnb-winrate-control">
      <div className="bnb-winrate-head">
        <div className="bnb-field-label"><IconTargetArrow size={17} /><strong>Winrate</strong><HelpTip title="Winrate" text="Share of profitable tokens among tokens with at least one sell. Percent values use 0–100, not 0–1." /></div>
        <div className="bnb-winrate-value"><strong>{min}%</strong><span>to</span><strong>{max}%</strong></div>
      </div>
      <div className="bnb-dual-range" style={{ '--range-start': `${min}%`, '--range-end': `${max}%` } as React.CSSProperties}>
        <div className="bnb-range-track"><i /></div>
        <input aria-label="Minimum winrate" type="range" min="0" max="100" step="1" value={min} onChange={(event) => setMin(Number(event.currentTarget.value))} />
        <input aria-label="Maximum winrate" type="range" min="0" max="100" step="1" value={max} onChange={(event) => setMax(Number(event.currentTarget.value))} />
      </div>
      <div className="bnb-scale-labels"><span>0</span><span>25</span><span>50</span><span>75</span><span>100%</span></div>
      <WinrateSpectrum buckets={spectrum} loading={spectrumLoading} error={spectrumError} min={min} max={max} period={period} />
      <div className="bnb-suggestion-row bnb-centered-suggestions">
        <button type="button" onClick={() => { onChange('min_winrate', 40); onChange('max_winrate', null); }}>40%+</button>
        <button type="button" onClick={() => { onChange('min_winrate', 50); onChange('max_winrate', 85); }}>50–85%</button>
        <button type="button" onClick={() => { onChange('min_winrate', 60); onChange('max_winrate', null); }}>60%+</button>
        <button type="button" onClick={() => { onChange('min_winrate', null); onChange('max_winrate', null); }}>Any</button>
      </div>
    </div>
  );
}

function DurationInput({ label, value, placeholder, onChange }: {
  label: string;
  value: number | null | undefined;
  placeholder: string;
  onChange: (value: number | null) => void;
}) {
  const [text, setText] = useState(formatDuration(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => setText(formatDuration(value)), [value]);

  const commit = () => {
    if (!text.trim()) {
      setInvalid(false);
      onChange(null);
      return;
    }
    const parsed = parseDuration(text);
    if (parsed == null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setText(formatDuration(parsed));
    onChange(parsed);
  };

  return (
    <label className={invalid ? 'invalid' : ''}>
      <span>{label}</span>
      <input value={text} placeholder={placeholder} onChange={(event) => setText(event.currentTarget.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
    </label>
  );
}

function DurationWindow({ title, help, min, max, onMinChange, onMaxChange, onExample, presets }: {
  title: string;
  help: string;
  min: number | null | undefined;
  max: number | null | undefined;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  onExample?: () => void;
  presets: Array<{ label: string; min: number | null; max: number | null }>;
}) {
  return (
    <div className={`bnb-duration-window ${min != null || max != null ? 'active' : ''}`}>
      <div className="bnb-smart-field-head">
        <div className="bnb-field-label"><IconClock size={16} /><strong>{title}</strong><HelpTip title={title} text={help} /></div>
        {onExample && <button className="bnb-example-link" type="button" onClick={onExample}>Show example <IconExternalLink size={12} /></button>}
      </div>
      <div className="bnb-duration-inputs">
        <DurationInput label="AT LEAST" value={min} placeholder="e.g. 1h" onChange={onMinChange} />
        <div className="bnb-duration-line"><i /></div>
        <DurationInput label="AT MOST" value={max} placeholder="e.g. 7d" onChange={onMaxChange} />
      </div>
      <div className="bnb-suggestion-row">
        {presets.map((preset) => <button type="button" key={preset.label} onClick={() => { onMinChange(preset.min); onMaxChange(preset.max); }}>{preset.label}</button>)}
        <button type="button" onClick={() => { onMinChange(null); onMaxChange(null); }}>Clear</button>
      </div>
    </div>
  );
}

function RocketsControl({ filters, onChange }: {
  filters: BnbFilters;
  onChange: (key: BnbScalarFilterKey, value: number | null) => void;
}) {
  const rockets: Array<{ label: string; min: BnbScalarFilterKey; max: BnbScalarFilterKey; note: string }> = [
    { label: '×2', min: 'min_rocket_x2_count', max: 'max_rocket_x2_count', note: 'ROI ≥ 200%' },
    { label: '×5', min: 'min_rocket_x5_count', max: 'max_rocket_x5_count', note: 'ROI ≥ 500%' },
    { label: '×10', min: 'min_rocket_x10_count', max: 'max_rocket_x10_count', note: 'ROI ≥ 1000%' },
  ];
  return (
    <div className="bnb-rockets-control">
      <div className="bnb-field-label"><IconRocket size={17} /><strong>Rockets</strong><HelpTip title="Rockets" text="Count of sold tokens that reached the selected lifetime ROI threshold." /></div>
      <div className="bnb-rocket-grid">
        {rockets.map((rocket) => (
          <div className="bnb-rocket-cell" key={rocket.label}>
            <div><strong>{rocket.label}</strong><span>{rocket.note}</span></div>
            <label><span>MIN</span><input type="number" min="0" step="1" placeholder="Any" value={filters[rocket.min] ?? ''} onChange={(event) => onChange(rocket.min, readNumber(event.currentTarget.value))} /></label>
            <label><span>MAX</span><input type="number" min="0" step="1" placeholder="Any" value={filters[rocket.max] ?? ''} onChange={(event) => onChange(rocket.max, readNumber(event.currentTarget.value))} /></label>
          </div>
        ))}
      </div>
    </div>
  );
}

function TradeProvidersEditor({ value, onChange }: { value: BnbMetricMap; onChange: (value: BnbMetricMap) => void }) {
  const rows = Object.entries(value);
  const update = (index: number, key: string, range: { min: number | null; max: number | null }) => {
    onChange(Object.fromEntries(rows.map(([rowKey, rowRange], rowIndex) => rowIndex === index ? [key, range] : [rowKey, rowRange])));
  };
  const add = () => {
    let index = rows.length + 1;
    let key = index === 1 ? 'direct' : `router_${index}`;
    while (value[key]) { index += 1; key = `router_${index}`; }
    onChange({ ...value, [key]: { min: 1, max: null } });
  };
  return (
    <div className="bnb-provider-editor">
      <div className="bnb-smart-field-head">
        <div className="bnb-field-label"><IconRoute size={16} /><strong>Trade providers</strong><HelpTip title="Trade providers" text="Require swaps through direct execution or a specific router. Every configured provider is combined with AND." /></div>
        <button className="bnb-example-link" type="button" onClick={add}><IconPlus size={13} /> Add</button>
      </div>
      {rows.length === 0 ? <button className="bnb-provider-empty" type="button" onClick={add}>No provider rules · add one</button> : (
        <div className="bnb-provider-rows">
          {rows.map(([key, range], index) => (
            <div className="bnb-provider-row" key={index}>
              <input value={key} maxLength={128} onChange={(event) => update(index, event.currentTarget.value, range)} aria-label="Provider key" />
              <input type="number" step="1" placeholder="Min" value={range.min ?? ''} onChange={(event) => update(index, key, { ...range, min: readNumber(event.currentTarget.value) })} aria-label={`${key} minimum`} />
              <input type="number" step="1" placeholder="Max" value={range.max ?? ''} onChange={(event) => update(index, key, { ...range, max: readNumber(event.currentTarget.value) })} aria-label={`${key} maximum`} />
              <button type="button" onClick={() => onChange(Object.fromEntries(rows.filter((_, rowIndex) => rowIndex !== index)))} aria-label={`Remove ${key}`}><IconX size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExampleModal({ mode, period, onClose }: { mode: 'period' | 'activity' | 'wallet'; period: BnbPeriod; onClose: () => void }) {
  const copy = mode === 'period' ? {
    title: 'How the activity period works',
    body: `A ${period}-day period selects tokens whose last activity falls inside the window before now. Each selected token keeps its lifetime PNL and ROI.`,
    left: `${period}d ago`, right: 'Now', center: 'tokens included',
  } : mode === 'activity' ? {
    title: 'How last activity age works',
    body: '“At least” excludes wallets that traded too recently. “At most” excludes wallets that have been inactive too long. Set both to create an age window.',
    left: 'At most', right: 'At least', center: 'last swap allowed',
  } : {
    title: 'How wallet age works',
    body: 'Wallet age is measured from the first observed swap. “At least 30d” keeps wallets that are 30 days old or older; add “At most” to exclude very old wallets.',
    left: 'Older', right: 'Newer', center: 'first swap allowed',
  };
  return (
    <div className="bnb-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="bnb-example-modal" role="dialog" aria-modal="true" aria-label={copy.title} onMouseDown={(event) => event.stopPropagation()}>
        <button className="bnb-modal-close" type="button" onClick={onClose}><IconX size={18} /></button>
        <div className="bnb-modal-icon"><IconClock size={22} /></div>
        <h2>{copy.title}</h2>
        <p>{copy.body}</p>
        <div className="bnb-timeline-demo">
          <div className="bnb-timeline-labels"><span>{copy.left}</span><span>{copy.right}</span></div>
          <div className="bnb-timeline-axis"><i /><b /><em /></div>
          <strong>{copy.center}</strong>
        </div>
        <div className="bnb-modal-note"><IconInfoCircle size={16} /><span>Time values accept compact notation: <b>1d2h30m</b>, <b>12h</b> or raw seconds.</span></div>
      </div>
    </div>
  );
}

export const BnbPresets: React.FC = () => {
  const [presets, setPresets] = useState<BnbPreset[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<BnbPreset | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['token-edge', 'activity']));
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tokenExportOpen, setTokenExportOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [example, setExample] = useState<'period' | 'activity' | 'wallet' | null>(null);
  const [exportSeed, setExportSeed] = useState<BnbExportCreated | null>(null);
  const [exportTask, setExportTask] = useState<BnbExportTask | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [winrateSpectrum, setWinrateSpectrum] = useState<BnbWinrateBucket[] | null>(null);
  const [winrateSpectrumLoading, setWinrateSpectrumLoading] = useState(true);
  const [winrateSpectrumError, setWinrateSpectrumError] = useState<string | null>(null);
  const winrateSpectrumCache = useRef(new Map<BnbPeriod, BnbWinrateBucket[]>());

  const selectedSavedPreset = useMemo(() => presets.find((preset) => preset.id === selectedId) || null, [presets, selectedId]);
  const isDirty = Boolean(draft && selectedSavedPreset && JSON.stringify(draft) !== JSON.stringify(selectedSavedPreset));
  const tokenAddresses = useMemo(() => extractBnbTokenAddresses(tokenInput), [tokenInput]);
  const rawTokenMatches = useMemo(() => tokenInput.match(BNB_TOKEN_ADDRESS_PATTERN)?.length || 0, [tokenInput]);
  const duplicateTokenCount = rawTokenMatches - tokenAddresses.length;
  const hasTokenActivityWindow = Boolean(draft && (
    draft.filters.last_activity_min_age_seconds != null
    || draft.filters.last_activity_max_age_seconds != null
  ));

  useEffect(() => {
    const telegramWindow = window as Window & { Telegram?: { WebApp?: { expand?: () => void } } };
    telegramWindow.Telegram?.WebApp?.expand?.();
    let mounted = true;
    bnbPresetsClient.getPresets()
      .then((data) => {
        if (!mounted) return;
        const normalized = data.map(normalizePreset);
        const selected = normalized.find((preset) => preset.is_active_preset) || normalized[0] || null;
        setPresets(normalized);
        setSelectedId(selected?.id || null);
        setDraft(selected ? clonePreset(selected) : null);
      })
      .catch((error: Error) => mounted && setNotice({ tone: 'error', text: error.message }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const period = draft?.period_days;
    if (!period) return;
    const controller = new AbortController();
    let canceled = false;
    const hasCachedSpectrum = winrateSpectrumCache.current.has(period);
    const cachedSpectrum = winrateSpectrumCache.current.get(period);
    if (hasCachedSpectrum) setWinrateSpectrum(cachedSpectrum || []);
    else setWinrateSpectrum(null);
    setWinrateSpectrumLoading(!hasCachedSpectrum);
    setWinrateSpectrumError(null);
    bnbPresetsClient.getWinrateSpectrum(period, controller.signal)
      .then((data) => {
        if (canceled) return;
        const buckets = Array.isArray(data) ? data
          .filter((bucket) => Number.isInteger(bucket?.winrate_percent)
            && bucket.winrate_percent >= 0 && bucket.winrate_percent <= 100
            && Number.isFinite(bucket?.wallets_count) && bucket.wallets_count >= 0
            && Number.isFinite(bucket?.percent) && bucket.percent >= 0)
          .sort((left, right) => left.winrate_percent - right.winrate_percent) : [];
        winrateSpectrumCache.current.set(period, buckets);
        setWinrateSpectrum(buckets);
        setWinrateSpectrumError(null);
        setWinrateSpectrumLoading(false);
      })
      .catch((error) => {
        if (canceled || (error instanceof Error && error.name === 'AbortError')) return;
        if (hasCachedSpectrum) {
          setWinrateSpectrumLoading(false);
          return;
        }
        setWinrateSpectrum(null);
        setWinrateSpectrumError(error instanceof Error ? error.message : 'Could not load the winrate spectrum.');
        setWinrateSpectrumLoading(false);
      });
    return () => { canceled = true; controller.abort(); };
  }, [draft?.period_days]);

  useEffect(() => {
    if (!activeTaskId) return;
    let canceled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const task = await bnbPresetsClient.getExportTask(activeTaskId);
        if (canceled) return;
        setExportTask(task);
        if (!TERMINAL_EXPORT_STATUSES.has(task.status)) timer = setTimeout(poll, 2500);
      } catch (error) {
        if (!canceled) setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not refresh export status.' });
      }
    };
    timer = setTimeout(poll, 1200);
    return () => { canceled = true; if (timer) clearTimeout(timer); };
  }, [activeTaskId]);

  const updateTopLevel = <K extends keyof BnbPreset>(key: K, value: BnbPreset[K]) => setDraft((current) => current ? { ...current, [key]: value } : current);
  const updateFilter = (key: BnbScalarFilterKey, value: number | null) => setDraft((current) => current ? { ...current, filters: { ...current.filters, [key]: value } } : current);
  const updateProviders = (value: BnbMetricMap) => setDraft((current) => current ? { ...current, filters: { ...current.filters, trade_providers: value } } : current);
  const updateBnbBalance = (key: 'bnb_balance_min' | 'bnb_balance_max', value: string | null) => setDraft((current) => current ? {
    ...current, filters: { ...current.filters, [key]: value },
  } : current);

  const selectPreset = (id: number) => {
    const preset = presets.find((item) => item.id === id);
    if (!preset) return;
    setSelectedId(id);
    setDraft(clonePreset(preset));
    setDeleteArmed(false);
    setTokenExportOpen(false);
    setNotice(null);
  };

  const allVisibleRanges = [...PERFORMANCE_RANGES, ...FILTER_SECTIONS.flatMap((section) => section.ranges), ...DURATION_RANGES];
  const validate = (preset: BnbPreset) => {
    const errors: string[] = [];
    const name = preset.name.trim();
    if (name.length < 3 || name.length > 32 || !/^[\w\u0400-\u04FF -]+$/.test(name)) errors.push('Preset name must be 3–32 characters and use letters, numbers, spaces, _ or -.');
    if (!Number.isInteger(preset.tokens_per_wallet) || preset.tokens_per_wallet < 1 || preset.tokens_per_wallet > 1000) errors.push('Tokens per wallet must be an integer from 1 to 1000.');
    BNB_SCALAR_FILTER_KEYS.forEach((key) => {
      const value = preset.filters[key];
      if (value != null && !Number.isFinite(value)) errors.push(`${key}: value must be a finite number.`);
    });
    allVisibleRanges.forEach((range) => {
      const min = preset.filters[range.min];
      const max = preset.filters[range.max];
      if (min != null && max != null && min > max) errors.push(`${range.label}: minimum cannot be greater than maximum.`);
      if (range.step === 1 && ((min != null && !Number.isInteger(min)) || (max != null && !Number.isInteger(max)))) errors.push(`${range.label}: use whole numbers.`);
    });
    ([
      ['Winrate', 'min_winrate', 'max_winrate'],
      ['Rockets ×2', 'min_rocket_x2_count', 'max_rocket_x2_count'],
      ['Rockets ×5', 'min_rocket_x5_count', 'max_rocket_x5_count'],
      ['Rockets ×10', 'min_rocket_x10_count', 'max_rocket_x10_count'],
    ] as Array<[string, BnbScalarFilterKey, BnbScalarFilterKey]>).forEach(([label, minKey, maxKey]) => {
      const min = preset.filters[minKey];
      const max = preset.filters[maxKey];
      if (min != null && max != null && min > max) errors.push(`${label}: minimum cannot exceed maximum.`);
      if (label.startsWith('Rockets') && ((min != null && !Number.isInteger(min)) || (max != null && !Number.isInteger(max)))) errors.push(`${label}: use whole numbers.`);
    });
    const lastMin = preset.filters.last_activity_min_age_seconds;
    const lastMax = preset.filters.last_activity_max_age_seconds;
    if (lastMin != null && lastMax != null && lastMin > lastMax) errors.push('Last activity: “at least” cannot exceed “at most”.');
    const walletMin = preset.filters.wallet_age_min_seconds;
    const walletMax = preset.filters.wallet_age_max_seconds;
    if (walletMin != null && walletMax != null && walletMin > walletMax) errors.push('Wallet age: “at least” cannot exceed “at most”.');
    const bnbMin = preset.filters.bnb_balance_min;
    const bnbMax = preset.filters.bnb_balance_max;
    if (bnbMin != null && !isBnbAmount(bnbMin)) errors.push('BNB balance: minimum must use up to 18 decimal places.');
    if (bnbMax != null && !isBnbAmount(bnbMax)) errors.push('BNB balance: maximum must use up to 18 decimal places.');
    if (bnbMin != null && bnbMax != null && isBnbAmount(bnbMin) && isBnbAmount(bnbMax) && compareBnbAmounts(bnbMin, bnbMax) > 0) {
      errors.push('BNB balance: minimum cannot exceed maximum.');
    }
    Object.entries(preset.filters.trade_providers).forEach(([key, range]) => {
      if (!/^[A-Za-z0-9_.:/-]{1,128}$/.test(key)) errors.push(`Trade provider “${key}” contains unsupported characters.`);
      if (range.min != null && range.max != null && range.min > range.max) errors.push(`Trade provider ${key}: minimum cannot exceed maximum.`);
    });
    return errors;
  };

  const applySavedPreset = (saved: BnbPreset) => {
    const normalized = normalizePreset(saved);
    setPresets((current) => current.map((preset) => preset.id === normalized.id ? normalized : normalized.is_active_preset ? { ...preset, is_active_preset: false } : preset));
    setDraft(clonePreset(normalized));
    setSelectedId(normalized.id);
  };

  const savePreset = async (preset: BnbPreset) => {
    const errors = validate(preset);
    if (errors.length) throw new Error(errors[0]);
    return bnbPresetsClient.updatePreset(preset.id, { ...toPayload(preset), name: preset.name.trim() });
  };

  const handleSave = async () => {
    if (!draft) return;
    setAction('save'); setNotice(null);
    try {
      applySavedPreset(await savePreset(draft));
      setNotice({ tone: 'success', text: 'Preset saved.' });
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not save preset.' });
    } finally { setAction(null); }
  };

  const handleActivate = async () => {
    if (!draft || draft.is_active_preset) return;
    setAction('activate'); setNotice(null);
    try {
      const saved = await savePreset({ ...draft, is_active_preset: true });
      applySavedPreset(saved);
      setNotice({ tone: 'success', text: 'This preset is now active for BNB files.' });
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not activate preset.' });
    } finally { setAction(null); }
  };

  const handleCreate = async () => {
    if (presets.length >= 10) { setNotice({ tone: 'error', text: 'You already have the maximum of 10 presets.' }); return; }
    const names = new Set(presets.map((preset) => preset.name.toLowerCase()));
    let index = 1; while (names.has(`preset ${index}`)) index += 1;
    setAction('create');
    try {
      const created = normalizePreset(await bnbPresetsClient.createPreset({ ...DEFAULT_BNB_PRESET, name: `Preset ${index}` }));
      setPresets((current) => [...current, created]); setSelectedId(created.id); setDraft(clonePreset(created));
      setNotice({ tone: 'success', text: 'New preset created.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not create preset.' }); }
    finally { setAction(null); }
  };

  const makeCopyName = (name: string) => {
    const names = new Set(presets.map((preset) => preset.name.toLowerCase()));
    let index = 1; let candidate = `${name.slice(0, 25)} copy`;
    while (names.has(candidate.toLowerCase())) { index += 1; candidate = `${name.slice(0, 23)} copy ${index}`; }
    return candidate.slice(0, 32);
  };

  const handleCopy = async () => {
    if (!draft || presets.length >= 10) { setNotice({ tone: 'error', text: 'Preset cannot be copied: the 10 preset limit is reached.' }); return; }
    setAction('copy');
    try {
      const created = normalizePreset(await bnbPresetsClient.createPreset({ ...toPayload(draft), name: makeCopyName(draft.name), is_active_preset: false }));
      setPresets((current) => [...current, created]); setSelectedId(created.id); setDraft(clonePreset(created));
      setNotice({ tone: 'success', text: 'Preset copied.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not copy preset.' }); }
    finally { setAction(null); }
  };

  const handleDelete = async () => {
    if (!draft || draft.name.toLowerCase() === 'base') return;
    if (!deleteArmed) { setDeleteArmed(true); return; }
    setAction('delete');
    try {
      await bnbPresetsClient.deletePreset(draft.id);
      const remaining = presets.filter((preset) => preset.id !== draft.id);
      const next = remaining.find((preset) => preset.is_active_preset) || remaining[0] || null;
      setPresets(remaining); setSelectedId(next?.id || null); setDraft(next ? clonePreset(next) : null); setDeleteArmed(false);
      setNotice({ tone: 'success', text: 'Preset deleted.' });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not delete preset.' }); }
    finally { setAction(null); }
  };

  const handleExport = async (tokens?: string[]) => {
    if (!draft) return;
    if (tokens && tokens.length === 0) {
      setNotice({ tone: 'error', text: 'Paste at least one valid 0x token address.' });
      return;
    }
    if (tokens && tokens.length > 5000) {
      setNotice({ tone: 'error', text: 'Token exports accept no more than 5,000 unique addresses.' });
      return;
    }
    if (tokens && !hasTokenActivityWindow) {
      setNotice({ tone: 'error', text: 'Set at least one Last activity boundary before exporting from tokens.' });
      return;
    }
    setAction(tokens ? 'token-export' : 'export'); setNotice(null); setExportSeed(null); setExportTask(null); setActiveTaskId(null);
    try {
      let exportPreset = draft;
      if (isDirty) { exportPreset = await savePreset(draft); applySavedPreset(exportPreset); }
      const created = await bnbPresetsClient.exportWallets(exportPreset.id, tokens);
      setExportSeed(created); setActiveTaskId(created.task_id);
      if (tokens) setTokenExportOpen(false);
      setNotice({
        tone: 'info',
        text: created.queued_for_release
          ? 'Export is queued until the release finishes.'
          : tokens
            ? `Export started for ${tokens.length.toLocaleString()} token contracts. Status will update here.`
            : 'Export started. Status will update here.',
      });
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Could not start export.' }); }
    finally { setAction(null); }
  };

  const clearAllFilters = () => setDraft((current) => current ? {
    ...current,
    filters: {
      ...current.filters,
      ...Object.fromEntries(BNB_SCALAR_FILTER_KEYS.map((key) => [key, null])),
      trade_providers: {}, quote_tokens: {}, projects: {},
      bnb_balance_min: null, bnb_balance_max: null,
    },
  } : current);

  const rangeIsActive = (range: RangeDefinition) => Boolean(draft && (draft.filters[range.min] != null || draft.filters[range.max] != null));
  const sectionCount = (section: FilterSectionDefinition) => section.ranges.reduce((count, range) => count + Number(rangeIsActive(range)), 0);
  const filterCount = draft ? [
    Number(draft.filters.min_winrate != null || draft.filters.max_winrate != null),
    Number(draft.filters.last_activity_min_age_seconds != null || draft.filters.last_activity_max_age_seconds != null),
    Number(draft.filters.wallet_age_min_seconds != null || draft.filters.wallet_age_max_seconds != null),
    Number(draft.filters.min_rocket_x2_count != null || draft.filters.max_rocket_x2_count != null),
    Number(draft.filters.min_rocket_x5_count != null || draft.filters.max_rocket_x5_count != null),
    Number(draft.filters.min_rocket_x10_count != null || draft.filters.max_rocket_x10_count != null),
    Number(draft.filters.bnb_balance_min != null || draft.filters.bnb_balance_max != null),
    ...allVisibleRanges.map((range) => Number(rangeIsActive(range))),
    Object.keys(draft.filters.trade_providers).length,
  ].reduce((total, count) => total + count, 0) : 0;
  const taskStatus = exportTask?.status || exportSeed?.status;
  const progress = exportTask?.total_rows ? Math.min(100, Math.round((exportTask.processed_rows / exportTask.total_rows) * 100)) : 0;

  if (loading) return <main className="bnb-page bnb-loading-page"><div className="bnb-loader-mark"><IconSparkles size={24} /></div><p>Loading BNB presets…</p></main>;

  return (
    <main className="bnb-page">
      <div className="bnb-page-glow" /><div className="bnb-noise" />
      <div className="bnb-shell">
        <header className="bnb-hero">
          <div className="bnb-brand-mark"><IconBrandBinance size={27} /></div>
          <div><div className="bnb-eyebrow"><span /> BNB CHAIN ANALYTICS</div><h1>BNB <em>Deep</em></h1></div>
          <div className="bnb-network-pill"><i /> LIVE</div>
        </header>

        {notice && <div className={`bnb-notice bnb-notice-${notice.tone}`} role="status"><IconInfoCircle size={17} /><span>{notice.text}</span><button type="button" onClick={() => setNotice(null)}><IconX size={15} /></button></div>}

        <section className="bnb-preset-toolbar bnb-glass-card">
          <div className="bnb-preset-line">
            <div className="bnb-preset-choice">
              <div className="bnb-select-shell bnb-preset-select">
                <select value={selectedId ?? ''} onChange={(event) => selectPreset(Number(event.currentTarget.value))} aria-label="Preset">
                  {presets.map((preset) => <option value={preset.id} key={preset.id}>{preset.is_active_preset ? '● ' : ''}{preset.name}</option>)}
                </select><IconChevronDown size={16} />
              </div>
              {draft?.is_active_preset
                ? <div className="bnb-active-preset"><i /><span>Active</span></div>
                : draft && <button className="bnb-activate-button" type="button" onClick={handleActivate} disabled={Boolean(action)}><IconCheck size={15} /><span>Make active</span></button>}
            </div>
            <div className="bnb-preset-actions">
              <button type="button" title="New preset" onClick={handleCreate} disabled={Boolean(action) || presets.length >= 10}><IconPlus size={16} /><span>New</span></button>
              <button type="button" title="Copy preset" onClick={handleCopy} disabled={!draft || Boolean(action) || presets.length >= 10}><IconCopy size={15} /><span>Copy</span></button>
              <button type="button" title="Delete preset" className={deleteArmed ? 'danger' : ''} onClick={handleDelete} onBlur={() => setDeleteArmed(false)} disabled={!draft || draft.name.toLowerCase() === 'base' || Boolean(action)}><IconTrash size={15} /><span>{deleteArmed ? 'Sure?' : 'Delete'}</span></button>
            </div>
          </div>
          {draft && (
            <>
              <div className="bnb-preset-editor-row">
                <label className="bnb-preset-name-field"><span>PRESET NAME</span><input value={draft.name} maxLength={32} onChange={(event) => updateTopLevel('name', event.currentTarget.value)} /></label>
                <div className="bnb-preset-cta">
                  <div className="bnb-export-wrap">
                    <span className="bnb-export-spark one">+$</span><span className="bnb-export-spark two">↗</span><span className="bnb-export-spark three">$</span>
                    <span className="bnb-export-spark four">%</span><span className="bnb-export-spark five">▲</span><span className="bnb-export-spark six">+</span>
                    <button
                      className="bnb-export-button"
                      type="button"
                      title="Export the BNB wallet dataset with the selected preset. Files will be delivered in Telegram."
                      onClick={() => handleExport()}
                      disabled={Boolean(action)}
                    >
                      {action === 'export' ? <span className="bnb-button-spinner" /> : <IconDownload size={18} />}
                      {action === 'export' ? 'Starting…' : 'Export wallets'}
                      <small>DAO</small>
                    </button>
                  </div>
                  <button
                    className={`bnb-token-export-toggle ${tokenExportOpen ? 'active' : ''}`}
                    type="button"
                    aria-expanded={tokenExportOpen}
                    aria-controls="bnb-token-export-panel"
                    onClick={() => setTokenExportOpen((open) => !open)}
                    disabled={Boolean(action)}
                  >
                    <IconCoin size={14} /><span>Export from tokens</span>
                  </button>
                  <button className="bnb-view-settings-button" type="button" onClick={() => setSettingsOpen(true)}><IconAdjustments size={15} /><span>View settings</span></button>
                </div>
              </div>

              {tokenExportOpen && (
                <div className="bnb-token-export-panel" id="bnb-token-export-panel">
                  <div className="bnb-token-export-head">
                    <div><strong>Token contracts</strong><span>Paste addresses or any text containing them.</span></div>
                    <button type="button" aria-label="Close token export" onClick={() => setTokenExportOpen(false)}><IconX size={15} /></button>
                  </div>
                  <textarea
                    autoFocus
                    aria-label="Token contracts"
                    value={tokenInput}
                    maxLength={300000}
                    placeholder={'0x1111…1111\n0x2222…2222'}
                    onChange={(event) => setTokenInput(event.currentTarget.value)}
                  />
                  <div className="bnb-token-export-footer">
                    <div
                      className={`bnb-token-count ${tokenAddresses.length > 5000 || !hasTokenActivityWindow ? 'has-warning' : ''}`}
                      aria-label={`${tokenAddresses.length} unique ${tokenAddresses.length === 1 ? 'token' : 'tokens'} found`}
                      aria-live="polite"
                    >
                      <span><strong>{tokenAddresses.length.toLocaleString()}</strong> unique {tokenAddresses.length === 1 ? 'token' : 'tokens'} found</span>
                      {duplicateTokenCount > 0 && <small>{duplicateTokenCount.toLocaleString()} {duplicateTokenCount === 1 ? 'duplicate' : 'duplicates'} ignored</small>}
                      {tokenAddresses.length > 5000 && <small>Maximum is 5,000 tokens.</small>}
                      {!hasTokenActivityWindow && <small>Set at least one Last activity boundary below.</small>}
                    </div>
                    <button
                      className="bnb-token-export-submit"
                      type="button"
                      onClick={() => handleExport(tokenAddresses)}
                      disabled={Boolean(action) || tokenAddresses.length === 0 || tokenAddresses.length > 5000 || !hasTokenActivityWindow}
                    >
                      {action === 'token-export' ? <span className="bnb-button-spinner" /> : <IconDownload size={15} />}
                      {action === 'token-export' ? 'Starting…' : 'Start export'}
                    </button>
                  </div>
                </div>
              )}

              {taskStatus && (
                <div className={`bnb-export-status ${TERMINAL_EXPORT_STATUSES.has(taskStatus) ? `is-${taskStatus}` : ''}`}>
                  <div><i /><strong>{friendlyStatus(taskStatus)}</strong><span>{exportSeed?.task_id}</span></div>
                  {exportTask && !TERMINAL_EXPORT_STATUSES.has(exportTask.status) && <div className="bnb-progress"><i style={{ width: `${progress}%` }} /></div>}
                  {exportTask?.status === 'done' && <span>{exportTask.matched_rows.toLocaleString()} wallets matched · sent to Telegram</span>}
                  {exportTask?.status === 'error' && <span>Try again later or contact support.</span>}
                </div>
              )}
            </>
          )}
        </section>

        {draft ? (
          <>
            <section className="bnb-setup-card bnb-glass-card">
              <div className="bnb-config-grid">
                <div className="bnb-format-control">
                  <div className="bnb-control-title"><span>FILE FORMAT</span></div>
                  <FormatPicker value={draft.format_type} onChange={(value) => updateTopLevel('format_type', value)} />
                </div>
                <div className="bnb-period-control">
                  <div className="bnb-control-title"><span>ACTIVITY PERIOD</span><HelpTip title="Activity period" text="The period selects tokens by calendar age of their last activity. PNL and ROI for those tokens remain lifetime metrics." /><button type="button" onClick={() => setExample('period')}>Show example</button></div>
                  <div className="bnb-period-segments">{PERIODS.map((period) => <button type="button" aria-label={`${period}d`} key={period} className={draft.period_days === period ? 'active' : ''} onClick={() => updateTopLevel('period_days', period)}>{period}<span>d</span></button>)}</div>
                </div>
              </div>
              <div className="bnb-setup-footer">
                <Toggle checked={draft.is_active} onChange={(value) => updateTopLevel('is_active', value)} label="Apply filters" detail="Turn off to export the period without metric filters" />
                <div className="bnb-filter-summary"><span className="bnb-filter-count"><strong>{filterCount}</strong><span>{filterCount === 1 ? 'active filter' : 'active filters'}</span></span>{filterCount > 0 && <button type="button" onClick={clearAllFilters}>Clear all</button>}</div>
              </div>
            </section>

            {draft.is_active ? (
              <section className="bnb-filter-workspace">
                <div className="bnb-section-intro"><div><div className="bnb-eyebrow"><span /> CORE SIGNAL</div><h2>Start with the outcome</h2><p>Only the essentials are open. Add limits when they help; leave the rest empty.</p></div></div>
                <div className="bnb-performance-card bnb-glass-card">
                  <WinrateControl filters={draft.filters} onChange={updateFilter} spectrum={winrateSpectrum} spectrumLoading={winrateSpectrumLoading} spectrumError={winrateSpectrumError} period={draft.period_days} />
                </div>
                <div className="bnb-balance-card bnb-glass-card">
                  <BnbBalanceControl
                    min={draft.filters.bnb_balance_min}
                    max={draft.filters.bnb_balance_max}
                    onMinChange={(value) => updateBnbBalance('bnb_balance_min', value)}
                    onMaxChange={(value) => updateBnbBalance('bnb_balance_max', value)}
                  />
                </div>
                <div className="bnb-primary-ranges bnb-core-ranges-card bnb-glass-card">
                  {PERFORMANCE_RANGES.map((range) => <RangeField key={range.min} definition={range} filters={draft.filters} onChange={updateFilter} />)}
                </div>

                <div className="bnb-section-intro bnb-time-intro"><div><div className="bnb-eyebrow"><span /> TIME WINDOWS</div><h2>Recency without raw seconds</h2></div></div>
                <div className="bnb-time-card bnb-glass-card">
                  <DurationWindow
                    title="Last activity" help="Time since the wallet's last swap. Use both values to keep wallets that traded inside a specific age window."
                    min={draft.filters.last_activity_min_age_seconds} max={draft.filters.last_activity_max_age_seconds}
                    onMinChange={(value) => updateFilter('last_activity_min_age_seconds', value)} onMaxChange={(value) => updateFilter('last_activity_max_age_seconds', value)}
                    onExample={() => setExample('activity')}
                    presets={[{ label: 'Within 24h', min: null, max: 86400 }, { label: '1–7 days', min: 86400, max: 604800 }, { label: '7–30 days', min: 604800, max: 2592000 }]}
                  />
                  <DurationWindow
                    title="Wallet age" help="Time since the first observed swap. A minimum age removes freshly created wallets."
                    min={draft.filters.wallet_age_min_seconds} max={draft.filters.wallet_age_max_seconds}
                    onMinChange={(value) => updateFilter('wallet_age_min_seconds', value)} onMaxChange={(value) => updateFilter('wallet_age_max_seconds', value)}
                    onExample={() => setExample('wallet')}
                    presets={[{ label: '7d+', min: 604800, max: null }, { label: '30d+', min: 2592000, max: null }, { label: '30–180d', min: 2592000, max: 15552000 }]}
                  />
                </div>

                <div className="bnb-accordion-list">
                  {FILTER_SECTIONS.map((section) => {
                    const SectionIcon = section.icon;
                    const open = openSections.has(section.id);
                    const count = sectionCount(section);
                    return (
                      <article className={`bnb-filter-card ${open ? 'open' : ''}`} key={section.id}>
                        <button className="bnb-filter-header" type="button" onClick={() => setOpenSections((current) => { const next = new Set(current); next.has(section.id) ? next.delete(section.id) : next.add(section.id); return next; })}>
                          <span className="bnb-filter-icon"><SectionIcon className="bnb-filter-vector" size={18} /><span className="bnb-filter-emoji">{section.emoji}</span></span><span><strong>{section.title}</strong><small>{section.description}</small></span>{count > 0 && <b>{count}</b>}<IconChevronDown className="bnb-chevron" size={18} />
                        </button>
                        {open && <div className="bnb-filter-body">{section.ranges.map((range) => <RangeField key={range.min} definition={range} filters={draft.filters} onChange={updateFilter} />)}{section.id === 'quality' && <TradeProvidersEditor value={draft.filters.trade_providers} onChange={updateProviders} />}</div>}
                      </article>
                    );
                  })}

                  <article className={`bnb-filter-card ${openSections.has('timing') ? 'open' : ''}`}>
                    <button className="bnb-filter-header" type="button" onClick={() => setOpenSections((current) => { const next = new Set(current); next.has('timing') ? next.delete('timing') : next.add('timing'); return next; })}>
                      <span className="bnb-filter-icon"><IconClock className="bnb-filter-vector" size={18} /><span className="bnb-filter-emoji">⏱️</span></span><span><strong>Trade timing</strong><small>Human-readable holding and exit durations.</small></span><IconChevronDown className="bnb-chevron" size={18} />
                    </button>
                    {openSections.has('timing') && <div className="bnb-filter-body bnb-duration-stack">{DURATION_RANGES.map((range) => <DurationWindow key={range.min} title={range.label} help={range.help} min={draft.filters[range.min]} max={draft.filters[range.max]} onMinChange={(value) => updateFilter(range.min, value)} onMaxChange={(value) => updateFilter(range.max, value)} presets={[{ label: '1m–1h', min: 60, max: 3600 }, { label: '1h–1d', min: 3600, max: 86400 }, { label: '1–7 days', min: 86400, max: 604800 }]} />)}</div>}
                  </article>

                  <article className={`bnb-filter-card ${openSections.has('rockets') ? 'open' : ''}`}>
                    <button className="bnb-filter-header" type="button" onClick={() => setOpenSections((current) => { const next = new Set(current); next.has('rockets') ? next.delete('rockets') : next.add('rockets'); return next; })}>
                      <span className="bnb-filter-icon"><IconRocket className="bnb-filter-vector" size={18} /><span className="bnb-filter-emoji">🚀</span></span><span><strong>Rockets</strong><small>Count repeat ×2, ×5 and ×10 outcomes.</small></span><IconChevronDown className="bnb-chevron" size={18} />
                    </button>
                    {openSections.has('rockets') && <div className="bnb-filter-body"><RocketsControl filters={draft.filters} onChange={updateFilter} /></div>}
                  </article>
                </div>
              </section>
            ) : <div className="bnb-filters-off"><IconBan size={19} /><div><strong>Metric filters are paused</strong><span>The period and output settings will still be used.</span></div></div>}

            <div className="bnb-save-dock-spacer" />
            <div className="bnb-save-dock"><div className="bnb-save-state"><i className={isDirty ? 'dirty' : ''} /><span>{isDirty ? 'Unsaved changes' : 'All changes saved'}</span></div><button type="button" onClick={handleSave} disabled={!isDirty || Boolean(action)}>{action === 'save' ? <span className="bnb-button-spinner" /> : <IconDeviceFloppy size={17} />}{action === 'save' ? 'Saving…' : 'Save preset'}</button></div>

            {settingsOpen && (
              <div className="bnb-drawer-backdrop" onMouseDown={() => setSettingsOpen(false)}>
                <aside className="bnb-settings-drawer" role="dialog" aria-modal="true" aria-label="Output view settings" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="bnb-drawer-head"><div><div className="bnb-eyebrow"><span /> OUTPUT ONLY</div><h2>View settings</h2><p>These options change the files, not which wallets match.</p></div><button type="button" onClick={() => setSettingsOpen(false)}><IconX size={19} /></button></div>
                  <div className="bnb-drawer-section">
                    <h3>Token details</h3>
                    <Toggle checked={draft.include_token_details} onChange={(value) => updateTopLevel('include_token_details', value)} label="Include Tokens sheets" detail="Available for Excel exports" />
                    <label className="bnb-drawer-field"><span>Tokens per wallet</span><input type="number" min="1" max="1000" step="1" value={draft.tokens_per_wallet} onChange={(event) => updateTopLevel('tokens_per_wallet', Number(event.currentTarget.value))} /><small>1–1000; large exports may be reduced evenly.</small></label>
                  </div>
                  <div className="bnb-drawer-section">
                    <h3>Wallet summary</h3>
                    <label className="bnb-drawer-field"><span>Sort by</span><div className="bnb-select-shell"><select value={draft.summary_order_column} onChange={(event) => updateTopLevel('summary_order_column', event.currentTarget.value as BnbPreset['summary_order_column'])}>{SUMMARY_SORT_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><IconChevronDown size={16} /></div></label>
                    <Toggle checked={draft.summary_order_desc} onChange={(value) => updateTopLevel('summary_order_desc', value)} label="Descending order" detail="Highest values first" />
                  </div>
                  <div className="bnb-drawer-section">
                    <h3>Token sheets</h3>
                    <label className="bnb-drawer-field"><span>Sort by</span><div className="bnb-select-shell"><select value={draft.token_order_column} onChange={(event) => updateTopLevel('token_order_column', event.currentTarget.value as BnbPreset['token_order_column'])}>{TOKEN_SORT_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><IconChevronDown size={16} /></div></label>
                    <Toggle checked={draft.token_order_desc} onChange={(value) => updateTopLevel('token_order_desc', value)} label="Descending order" detail="Highest values first" />
                  </div>
                  <button className="bnb-drawer-done" type="button" onClick={() => setSettingsOpen(false)}><IconCheck size={17} /> Done</button>
                </aside>
              </div>
            )}

            {example && <ExampleModal mode={example} period={draft.period_days} onClose={() => setExample(null)} />}
          </>
        ) : <section className="bnb-empty-state bnb-glass-card"><IconWallet size={28} /><h2>No presets available</h2><button type="button" onClick={handleCreate}><IconPlus size={16} /> Create preset</button></section>}
      </div>
    </main>
  );
};

export default BnbPresets;
